import { defineComponent as _defineComponent } from 'vue'
import TradeConfirmation from './TradeConfirmation.vue';
import TradeFormGroup from './forms/TradeFormGroup.vue';
import {
  CreateTradeRequest,
  Beneficiary,
  beneficiaryName,
  getBuyCcy,
  VersionedObject,
  TradeFundingDetails,
  CreatePaymentRequest,
  AmountType,
} from 'ah-api-gateways';
import { TradeDestination } from './forms/tradeDestination';
import { TradeFundsDestination, TradeDetails } from '../models/trade';
import { TradingManagerErrorCodes, waitForEntityCreation } from 'ah-requests';
import { QuotePriceRequest } from '../requests/pricingRequest';
import { BModal } from 'bootstrap-vue';
import { of } from 'rxjs';
import { mergeMap, catchError, map } from 'rxjs/operators';
import { useRequestManager } from 'ah-common-lib/src/requestManager/useRequestManager';
import { useRoute, useRouter } from 'vue-router/composables';
import { useTradeState } from '..';
import { reactive, getCurrentInstance, watch, computed, ref } from 'vue';
import RouteProtectorModal from 'ah-common-lib/src/common/components/route/RouteProtectorModal.vue';
import { formatCurrencyValue } from 'ah-common-lib/src/helpers/currency';
import { useOnBehalfOf } from 'ah-common-lib/src/onBehalfOf/useInjectedOBO';
import { create } from 'lodash';
import { formatDate } from 'ah-common-lib/src/helpers/time';


export default /*#__PURE__*/_defineComponent({
  __name: 'TradeCreator',
  props: {
    title: { default: 'Create new FX Trade' },
    inModal: { type: [Boolean, String], default: false },
    beneficiary: null,
    forceKeep: { type: [Boolean, String], default: false },
    forceImmediateSpot: { type: [Boolean, String], default: false },
    syncTradeFunds: { type: [Boolean, String], default: false },
    allowedBuyCurrencies: null,
    allowedSellCurrencies: null,
    isPayment: { type: [String, Boolean], default: false }
  },
  emits: ["update:stage", "trade-cancelled", "flow-completed", "trade-created", "update:beneficiary"],
  setup(__props: any, { emit }: { emit: ({
  (e: 'update:stage', value: 'tradeDetails' | 'tradeConfirmation'): void;
  (e: 'trade-cancelled'): void;
  (e: 'flow-completed'): void;
  (e: 'trade-created', value: VersionedObject): void;
  (e: 'update:beneficiary', value: Beneficiary): void;
}), expose: any, slots: any, attrs: any }) {

const props = __props as { title: string, inModal: boolean | string, beneficiary?: Beneficiary, forceKeep: boolean | string, forceImmediateSpot: boolean | string, syncTradeFunds: boolean | string, allowedBuyCurrencies?: string[], allowedSellCurrencies?: string[], isPayment: string | boolean };

const confirmationModal = ref<InstanceType<typeof BModal> | null>(null);
const routeLeaveConfirmModal = ref<InstanceType<typeof BModal> | null>(null);





const state = reactive<{
  stage: 'tradeDetails' | 'tradeConfirmation';
  tradeDetails: TradeDetails | null;
  tradePrice: QuotePriceRequest | null;
  tradeDestination: TradeDestination | null;
  tradeFundingDetails: Partial<TradeFundingDetails>;
  confirmShowing: boolean;
  routeLeaveConfirmShowing: boolean;
}>({
  stage: 'tradeDetails',
  tradeDetails: null,
  tradePrice: null,
  tradeDestination: null,
  tradeFundingDetails: {},
  confirmShowing: false,
  routeLeaveConfirmShowing: false,
});

const requestManager = useRequestManager({
  exposeToParent: false,
});

const costTransparencyCurrency = ref<AmountType>(AmountType.BUY);

const tradeState = useTradeState();
const onBehalfOfClient = useOnBehalfOf();

const toast = getCurrentInstance()?.proxy.$toast;

const route = useRoute();
const router = useRouter();

function created() {
  if (route.query.tradeDetails || route.query.tradePrice) {
    try {
      if (route.query.tradeDetails) {
        state.tradeDetails = JSON.parse(route.query.tradeDetails as string);
      }
      if (route.query.tradePrice) {
        state.tradePrice = JSON.parse(route.query.tradePrice as string);
      }
    } catch (e) {}
    router.replace({ path: route.path });
  }

  tradeState.store.useSettingsStore().loadPaymentRails();
}

created();

/**
 * Gets all "load" states on the request manager (including children)
 */
const loadRequestStates = computed(() =>
  requestManager.getAllRequests((key) => key.startsWith('load'), true).map((r) => r.state)
);

const isTrade = computed(
  () => !state.tradeDetails || state.tradeDetails.sellCurrency !== state.tradeDetails.buyCurrency
);

const isInModal = computed(() => props.inModal !== false);

const isStackedView = computed(() => tradeState.mediaQuery.is('smDown') !== false);

const isForceKeep = computed(() => props.forceKeep !== false);

const isSyncTradeFunds = computed(() => props.syncTradeFunds !== false);

const isForceImmediateSpot = computed(() => props.forceImmediateSpot !== false);

const isOnBehalfOf = computed(() => onBehalfOfClient.value);

const isScheduledPayment = computed(() => props.isPayment !== false);

const warningMessage = computed(() => {
  if (isOnBehalfOf.value) {
    return `You are currently creating a new FX Trade for ${onBehalfOfClient.value?.name}. Are you sure you want to exit On Behalf Of mode?`;
  }
  return 'Are you sure you want to leave this page? Unsaved changes will be lost.';
});

const oboTradeConfirmationMessage = computed(() => {
  if (state.tradeDetails && props.beneficiary && onBehalfOfClient.value) {
    const beneficiary = beneficiaryName(props.beneficiary!);

    return `You are sending ${state.tradeDetails.buyCurrency} ${formatCurrencyValue(
      state.tradeDetails.amount!
    )} to ${beneficiary} on behalf of
    ${onBehalfOfClient.value.name}.`;
  }
  return undefined;
});

function onRetryLoad() {
  requestManager.getAllRequestsInState('error', true).forEach((r) => r.managerData.retryRequest(r.request));
}

function onStageChange() {
  emit('update:stage', state.stage);
}

watch(
  () => state.stage,
  () => {
    onStageChange();
  },
  { immediate: true }
);

function onTradeCancelled() {
  emit('trade-cancelled');
}

function onRevertToForm() {
  state.stage = 'tradeDetails';
}

function onOutdatedPrice() {
  toast?.error('Price request is outdated, please re-submit values to continue.');
  onRevertToForm();
}

function onTradeSubmitted() {
  if (isTrade.value) {
    state.stage = 'tradeConfirmation';
  } else {
    requestManager.manager.newPromise(
      'createTrade',
      tradeState.services.payments
        .createPayment(
          {
            walletId: state.tradeDetails!.walletId!,
            amount: state.tradeDetails!.amount!,
            beneficiaryId: props.beneficiary!.id,
            reference: state.tradeDestination?.reference || undefined,
            executionDate: state.tradeDestination?.executionDate,
            chargeType: state.tradeDestination?.chargeType,
            reason: state.tradeDestination?.reason || undefined,
          },
          onBehalfOfClient.value?.id
        )
        .toPromise()
        .then(() => tradeState.store.useWalletsStore().waitForWalletChange({ id: state.tradeDetails!.walletId! }))
        .then(() => {
          tradeState.toast.success('Funds sent successfully');
          state.tradePrice = null;
          state.tradeDestination = null;
          requestManager.manager.clear();
          state.stage = 'tradeDetails';
          emit('flow-completed');
        })
    );
  }
}

function clearFieldsAndFinish(version: VersionedObject) {
  tradeState.store.useWalletsStore().expireCollaterals();
  state.tradePrice = null;
  state.tradeDestination = null;
  state.tradeDetails = null;
  requestManager.manager.clear();
  emit('trade-created', version);
  state.stage = 'tradeDetails';
  emit('flow-completed');
}

function scheduleTradePayment(payload: CreatePaymentRequest, tradeVersionedObject: VersionedObject) {
  if (state.tradePrice && state.tradePrice.response) {
    const buyCurrency = getBuyCcy(state.tradePrice.response);

    requestManager.manager.newPromise(
      'schedulePayment',
      tradeState.store
        .useWalletsStore()
        .loadCurrencyWallet({
          currency: buyCurrency.currency,
          owner: onBehalfOfClient.value ? { id: onBehalfOfClient.value.id, isPartner: false } : undefined,
        })
        .then((buyWallet) => {
          if (buyWallet) {
            payload.walletId = buyWallet!.id;
            payload.amount = buyCurrency.clientAmount;
            return tradeState.services.payments
              .createScheduledPayment(payload, onBehalfOfClient.value?.id, {
                errors: { silent: true },
              })
              .toPromise();
          }
          throw 'No wallet found for client';
        })
        .then(() => tradeState.store.useWalletsStore().waitForWalletChange({ id: state.tradeDetails!.walletId! }))
        .then(
          () => {
            tradeState.toast.success('Trade and Payment created successfully');
          },
          () => {
            tradeState.toast.error('Funds have been converted, but payment creation has failed');
          }
        )
        .finally(() => clearFieldsAndFinish(tradeVersionedObject))
    );
  }
}

function onTradeConfirmed(bypassOBOConfirm = false) {
  if (isOnBehalfOf.value && props.beneficiary && !bypassOBOConfirm) {
    confirmationModal.value?.show();
    return;
  }

  tradeState.store
    .useAuthStore()
    .checkComplianceApproval({ showModal: true, client: onBehalfOfClient.value ?? undefined })
    .then(() => {
      let createTradeRequest: CreateTradeRequest = {
        priceId: state.tradePrice?.response!.id!,
        hedgingProduct: state.tradePrice?.instrument!,
        syncPendingFunds: isSyncTradeFunds.value,
      };

      const { valid, ...details } = state.tradeFundingDetails;
      createTradeRequest = { ...createTradeRequest, ...details };

      requestManager.manager
        .sameOrCancelAndNew(
          'createTrade',
          tradeState.services.trade
            .createTrade(createTradeRequest, onBehalfOfClient.value?.id, {
              errors: {
                errorMessages: [
                  {
                    message: `Client limit profile not found`,
                    name: 'client-limit-profile-not-found',
                    code: (response) => response?.data?.code === TradingManagerErrorCodes.LIMITS_PROFILE_MISSING,
                    toastType: 'danger',
                  },
                  {
                    message: `The quoted price for this trade has expired. Please refresh the price quote and try again.`,
                    name: 'trade-price-expired',
                    code: (response) => response?.data?.code === TradingManagerErrorCodes.PRICE_EXPIRED,
                    toastType: 'danger',
                  },
                ],
              },
            })
            .pipe(
              mergeMap((idEntity) =>
                waitForEntityCreation(() =>
                  tradeState.services.trade.getTrade(idEntity.id, { errors: { silent: true } })
                ).pipe(
                  catchError(() => of(idEntity)),
                  map(() => idEntity)
                )
              )
            ),
          createTradeRequest
        )
        .subscribe((tradeVersionedObject) => {
          if (state.tradeDestination) {
            let payload: CreatePaymentRequest = {
              beneficiaryId: state.tradeDestination.beneficiary?.id,
              reference: state.tradeDestination.reference,
              chargeType: state.tradeDestination.chargeType,
              reason: state.tradeDestination.reason,
            };
            if (isScheduledPayment.value) {
              // if current trade is a payment we should
              // make an withdraw after the trade is complete with the selected execution
              // date (always set to after the settlement date).
              payload.executionDate = state.tradeDestination!.executionDate
                ? formatDate(state.tradeDestination!.executionDate, 'yyyy-MM-dd')
                : formatDate(state.tradePrice?.response?.settlementDate, 'yyyy-MM-dd');

              scheduleTradePayment(payload, tradeVersionedObject);
            } else if (
              state.tradeDestination?.destination === TradeFundsDestination.SEND &&
              !isScheduledPayment.value
            ) {
              // if current trade has a linked payment
              // create a payment linked to the trade
              payload.tradeId = tradeVersionedObject.id;
              scheduleTradePayment(payload, tradeVersionedObject);
            } else {
              tradeState.toast.success('Trade created successfully');
              clearFieldsAndFinish(tradeVersionedObject);
            }
          }
        });
    });
}

function goBack() {
  router.push('./');
}

return { __sfc: true,confirmationModal, routeLeaveConfirmModal, props, emit, state, requestManager, costTransparencyCurrency, tradeState, onBehalfOfClient, toast, route, router, created, loadRequestStates, isTrade, isInModal, isStackedView, isForceKeep, isSyncTradeFunds, isForceImmediateSpot, isOnBehalfOf, isScheduledPayment, warningMessage, oboTradeConfirmationMessage, onRetryLoad, onStageChange, onTradeCancelled, onRevertToForm, onOutdatedPrice, onTradeSubmitted, clearFieldsAndFinish, scheduleTradePayment, onTradeConfirmed, goBack, TradeConfirmation, TradeFormGroup, BModal, RouteProtectorModal }
}

})